import { normalizeToCanonical } from './normalize-to-canonical'

const STRUCTURAL_TAGS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'p', 'br'])

const ALLOWED_ATTRS: Record<string, ReadonlySet<string>> = {
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan']),
  p: new Set(['id']),
}

/**
 * Reduce Word table HTML to the minimum structure the LLM needs for review:
 * table/tr/td/th, rowspan/colspan, and `<p id="uniqueLocalId">` anchors with cell text.
 * Classes, inline styles, and wrapper spans are removed to shrink reference token count.
 */
export function buildReviewTableHtml(html: string, targetCellIds: ReadonlySet<string>): string {
  const normalized = normalizeToCanonical(html)
  if (!normalized || !/<table[\s>]/i.test(normalized)) return normalized.trim()

  const doc = new DOMParser().parseFromString(normalized, 'text/html')
  const table = doc.querySelector('table')
  if (!table) return normalized.trim()

  unwrapNonStructuralElements(table)
  stripDisallowedAttributes(table, targetCellIds)

  return table.outerHTML
}

function unwrapNonStructuralElements(root: Element): void {
  const children = [...root.children]
  for (const child of children) {
    unwrapNonStructuralElements(child)
    const tag = child.tagName.toLowerCase()
    if (!STRUCTURAL_TAGS.has(tag)) {
      unwrapElement(child)
    }
  }
}

function unwrapElement(element: Element): void {
  const parent = element.parentNode
  if (!parent) return

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element)
  }
  parent.removeChild(element)
}

function stripDisallowedAttributes(root: Element, targetCellIds: ReadonlySet<string>): void {
  for (const element of [root, ...root.querySelectorAll('*')]) {
    const tag = element.tagName.toLowerCase()
    const allowed = ALLOWED_ATTRS[tag]
    for (const attr of [...element.attributes]) {
      const name = attr.name.toLowerCase()
      if (!allowed?.has(name)) {
        element.removeAttribute(attr.name)
        continue
      }
      // A cell paragraph outside the reviewed selection is reference-only: keep its
      // text so the model can still reason about the row, but drop its id so it can't
      // be mistaken for a paragraph the model is allowed to anchor a comment to.
      //
      // Why this matters more for tables than prose: per-cell anchoring is the
      // weakest measured LLM capability in this whole pipeline - TABVERSE reports a
      // mean cell-lookup accuracy of just 9.9% across 17 models. Removing ids from
      // out-of-scope cells doesn't just discourage mis-anchoring, it makes the
      // mistake unrepresentable - a cell with no id has nothing for the model to
      // copy in the first place.
      const isUntargetedCellParagraphId =
        tag === 'p' && name === 'id' && !targetCellIds.has(attr.value)
      if (isUntargetedCellParagraphId) {
        element.removeAttribute(attr.name)
      }
    }
  }
}
